import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Folder, Plus, Trash2, MoreHorizontal } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useData } from '../context/DataContext';
import { toast } from 'sonner';

const CollectionsPage = () => {
  const { collections, clients, createCollection, deleteCollection, fetchAds } = useData();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newCollection, setNewCollection] = useState({ name: '', description: '', client_id: '' });
  const [isLoading, setIsLoading] = useState(false);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newCollection.name.trim()) return;
    
    setIsLoading(true);
    try {
      await createCollection({
        ...newCollection,
        client_id: newCollection.client_id || null
      });
      setNewCollection({ name: '', description: '', client_id: '' });
      setIsDialogOpen(false);
      toast.success('Collection created');
    } catch (err) {
      toast.error('Failed to create collection');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (collectionId) => {
    if (window.confirm('Delete this collection? Ads will not be deleted.')) {
      try {
        await deleteCollection(collectionId);
        toast.success('Collection deleted');
      } catch (err) {
        toast.error('Failed to delete');
      }
    }
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client ? client.name : null;
  };

  return (
    <div className="min-h-screen" data-testid="collections-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <Folder className="w-7 h-7 text-violet-400" />
            <h1 className="text-2xl md:text-3xl font-bold text-white">Collections</h1>
          </div>
          <p className="text-zinc-500">Group related ads together</p>
        </div>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-violet-600 hover:bg-violet-500" data-testid="add-collection-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Collection
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-zinc-800">
            <DialogHeader>
              <DialogTitle className="text-white">Add Collection</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-4">
              <div>
                <Label className="text-zinc-400">Name</Label>
                <Input
                  data-testid="collection-name-input"
                  value={newCollection.name}
                  onChange={(e) => setNewCollection({ ...newCollection, name: e.target.value })}
                  placeholder="Collection name"
                  className="mt-1.5 bg-zinc-800 border-zinc-700 text-white"
                  required
                />
              </div>
              <div>
                <Label className="text-zinc-400">Description (optional)</Label>
                <Input
                  value={newCollection.description}
                  onChange={(e) => setNewCollection({ ...newCollection, description: e.target.value })}
                  placeholder="Brief description"
                  className="mt-1.5 bg-zinc-800 border-zinc-700 text-white"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Client (optional)</Label>
                <Select
                  value={newCollection.client_id || '_none_'}
                  onValueChange={(value) => setNewCollection({ ...newCollection, client_id: value === '_none_' ? '' : value })}
                >
                  <SelectTrigger className="mt-1.5 bg-zinc-800 border-zinc-700 text-zinc-200">
                    <SelectValue placeholder="Select client..." />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="_none_" className="text-zinc-400">None</SelectItem>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id} className="text-zinc-200">
                        {client.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-violet-600 hover:bg-violet-500"
                data-testid="create-collection-btn"
              >
                {isLoading ? 'Creating...' : 'Create Collection'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Collections Grid */}
      {collections.length === 0 ? (
        <div className="text-center py-20">
          <div className="w-20 h-20 rounded-full bg-zinc-800/50 flex items-center justify-center mx-auto mb-4">
            <Folder className="w-10 h-10 text-zinc-600" />
          </div>
          <h3 className="text-xl font-semibold text-zinc-300 mb-2">No collections yet</h3>
          <p className="text-zinc-500 mb-6">Create collections to group related ads</p>
          <Button
            onClick={() => setIsDialogOpen(true)}
            className="bg-violet-600 hover:bg-violet-500"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Collection
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {collections.map((collection, index) => (
            <motion.div
              key={collection.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="
                group bg-zinc-900/50 border border-white/5 rounded-xl p-5
                hover:border-violet-500/30 transition-colors
              "
              data-testid={`collection-card-${collection.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-violet-500/20 flex items-center justify-center">
                    <Folder className="w-5 h-5 text-violet-400" />
                  </div>
                  <div>
                    <h3 className="font-medium text-zinc-200">{collection.name}</h3>
                    {collection.client_id && (
                      <p className="text-sm text-zinc-500">
                        {getClientName(collection.client_id)}
                      </p>
                    )}
                  </div>
                </div>
                
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="opacity-0 group-hover:opacity-100 text-zinc-500 hover:text-white"
                    >
                      <MoreHorizontal className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-800">
                    <DropdownMenuItem
                      className="text-red-400 focus:text-red-300 focus:bg-zinc-800"
                      onClick={() => handleDelete(collection.id)}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              
              {collection.description && (
                <p className="mt-3 text-sm text-zinc-500 line-clamp-2">
                  {collection.description}
                </p>
              )}
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CollectionsPage;
